import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import CheckerBackground from "../components/CheckerBackground";
import {
  Flag, Gauge, CloudSun, BarChart3, Users, Timer, Check, ArrowRight, Clock,
  ShieldCheck, History as HistoryIcon, UserCircle, Trophy, Mail, Sparkles,
} from "lucide-react";

const ICON = "/mytrack-icon.png";
const SHOTS = {
  dashboard: "/marketing/dashboard.png",
  logRun: "/marketing/log-run.png",
  analytics: "/marketing/analytics.png",
  history: "/marketing/history.png",
  pitCrew: "/marketing/pit-crew.png",
  profile: "/marketing/profile.png",
};

const PAGES = [
  {
    icon: Gauge, tag: "Log Race", title: "Log a full run in seconds",
    copy: "Big, glove-friendly inputs built for the lanes. Capture reaction time, 60-ft, 330, 1/8 & 1/4 ET, trap speed, dial-in, opponent data, tire pressures and track conditions — the whole pass, one screen.",
    shot: SHOTS.logRun,
  },
  {
    icon: BarChart3, tag: "Analytics", title: "See exactly where you're gaining",
    copy: "Automatic charts turn your runs into insight — ET trends, reaction-time consistency, win/loss record and runs-by-track. Spot the patterns that shave hundredths off your pass.",
    shot: SHOTS.analytics,
  },
  {
    icon: HistoryIcon, tag: "History", title: "Every run, searchable",
    copy: "Your whole season in one place. Filter by wins, losses and byes, or search any track, class or note. Tap a run to see the full detail and tune from real data.",
    shot: SHOTS.history,
  },
  {
    icon: UserCircle, tag: "Your Garage", title: "Built around your car & tracks",
    copy: "Set up your vehicle, save your home tracks, and keep your racer profile ready. MyTrackApp shapes itself around how you run.",
    shot: SHOTS.profile,
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const go = () => navigate("/login");

  return (
    <div className="min-h-screen bg-mt-bg text-mt-text">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-mt-border bg-mt-bg/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <div className="flex items-center gap-2">
            <img src={ICON} alt="MyTrack" className="h-9 w-9 rounded-lg" />
            <span className="font-head text-lg font-black tracking-tight">MyTrack<span className="text-mt-primary">App</span></span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={go} data-testid="nav-signin" className="px-2 text-sm font-bold text-mt-text hover:text-mt-primary">Sign in</button>
            <Button onClick={go} data-testid="nav-cta" className="bg-mt-primary text-white hover:bg-mt-primary/90">Start free trial</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 py-14 md:grid-cols-2 md:py-20">
          <div>
            <div className="flex items-center gap-2 text-mt-primary">
              <Flag size={16} /> <span className="text-xs font-bold uppercase tracking-[0.25em]">Drag Racing Log & Analytics</span>
            </div>
            <h1 className="mt-3 font-head text-4xl font-black leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
              Log every run.<br />Improve every pass.
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-mt-mutedfg">
              The fast, no-nonsense companion for bracket and heads-up racers. Log runs trackside, track your season, invite your crew, and dial in your car with real data — right from your phone's browser. Nothing to install.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button onClick={go} data-testid="hero-start-trial" className="gap-2 bg-mt-primary px-10 py-8 text-lg font-black text-white shadow-lg shadow-mt-primary/25 hover:bg-mt-primary/90 sm:text-xl">
                Start your free 10-day trial <ArrowRight size={22} />
              </Button>
              <a href="#features" className="text-sm font-bold text-mt-text underline-offset-4 hover:text-mt-primary hover:underline">See everything it does ↓</a>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-semibold text-mt-mutedfg">
              <span className="flex items-center gap-1"><Clock size={14} /> No credit card to start</span>
              <span className="flex items-center gap-1"><ShieldCheck size={14} /> $24.95 one-time · no subscription</span>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-mt-primary/10 blur-2xl" />
            <div className="relative overflow-hidden rounded-2xl border border-mt-border bg-mt-card shadow-xl">
              <img src={SHOTS.dashboard} alt="MyTrackApp dashboard" className="w-full" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="border-y border-mt-border bg-mt-card/60">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 px-5 py-8 md:grid-cols-4">
          {[
            { icon: Gauge, label: "Full timing data" },
            { icon: BarChart3, label: "Season analytics" },
            { icon: Users, label: "Virtual Pit Crew" },
            { icon: CloudSun, label: "Weather auto-fill" },
          ].map((f) => (
            <div key={f.label} className="flex flex-col items-center gap-2 text-center">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-mt-primary text-white"><f.icon size={20} /></span>
              <span className="text-sm font-bold">{f.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== STANDOUT: Virtual Pit Crew ===== */}
      <section className="relative overflow-hidden bg-mt-text py-16 text-white md:py-20" data-testid="pitcrew-feature">
        <CheckerBackground />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 md:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-mt-accent px-3 py-1 text-xs font-black uppercase tracking-widest text-mt-text">
              <Sparkles size={14} /> Fan Favorite
            </span>
            <h2 className="mt-4 font-head text-3xl font-black tracking-tighter sm:text-4xl lg:text-5xl">
              Bring your <span className="text-mt-accent">Virtual Pit Crew</span>
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-white/75">
              Racing is better with your people. Invite family, friends and your crew chief by email so they can follow your season — your race times, win/loss record, reaction times and analytics. One tap sends a sharp branded invite. It's the most fun way to share your progress and keep everyone in the loop between rounds.
            </p>
            <ul className="mt-6 space-y-2">
              {["One-tap email invites to friends & crew", "Share your times, record & reaction stats", "Keep everyone hyped between rounds"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm font-semibold text-white/90">
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-mt-accent text-mt-text"><Check size={13} /></span>{x}
                </li>
              ))}
            </ul>
            <Button onClick={go} data-testid="pitcrew-cta" className="mt-7 gap-2 bg-mt-accent px-6 py-6 text-base font-black text-mt-text hover:bg-mt-accent/90">
              <Mail size={18} /> Invite your crew — start free
            </Button>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-mt-accent/20 blur-2xl" />
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-mt-card shadow-2xl">
              <img src={SHOTS.pitCrew} alt="Virtual Pit Crew invites" className="w-full" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Per-page feature sections */}
      <section id="features" className="mx-auto max-w-6xl px-5 py-16 md:py-20">
        <div className="mb-14 text-center">
          <h2 className="font-head text-3xl font-black tracking-tighter sm:text-4xl">Everything a racer needs — nothing they don't</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-mt-mutedfg">Six focused screens that just work, on any phone, tablet or laptop.</p>
        </div>
        <div className="space-y-20">
          {PAGES.map((f, i) => (
            <div key={f.title} className={`grid items-center gap-8 md:grid-cols-2 ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}>
              <div>
                <div className="flex items-center gap-2 text-mt-primary">
                  <span className="inline-grid h-10 w-10 place-items-center rounded-xl bg-mt-primary text-white"><f.icon size={18} /></span>
                  <span className="text-xs font-bold uppercase tracking-[0.25em]">{f.tag}</span>
                </div>
                <h3 className="mt-4 font-head text-2xl font-bold tracking-tight sm:text-3xl">{f.title}</h3>
                <p className="mt-3 max-w-md text-base leading-relaxed text-mt-mutedfg">{f.copy}</p>
                <button onClick={go} className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-mt-primary hover:underline">
                  Start free trial <ArrowRight size={15} />
                </button>
              </div>
              <div className="overflow-hidden rounded-2xl border border-mt-border bg-mt-card shadow-lg">
                <img src={f.shot} alt={f.title} className="w-full" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How the free trial works — LARGE cards */}
      <section className="relative overflow-hidden bg-mt-primary py-16 text-white md:py-24">
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-white/70">Try before you buy</span>
            <h2 className="mt-3 font-head text-3xl font-black tracking-tighter sm:text-5xl">How the free trial works</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-white/80">Full access for 10 days — that's two race weekends to put it to work before you decide.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { n: "1", icon: Flag, t: "Start your trial", d: "Sign in with just your email or Google — no password, no credit card. The entire app is yours, free, for 10 full days." },
              { n: "2", icon: Trophy, t: "Log your weekends", d: "Enter every run, watch your stats build, invite your pit crew, and see your season come to life with real analytics." },
              { n: "3", icon: ShieldCheck, t: "Buy to keep it", d: "Love it? Unlock forever for $24.95 and your data stays saved for good. Not ready? It simply goes read-only — nothing's lost." },
            ].map((s) => (
              <div key={s.n} className="flex flex-col rounded-3xl border border-white/15 bg-white/10 p-8 backdrop-blur-sm md:p-10">
                <div className="flex items-center justify-between">
                  <div className="grid h-14 w-14 place-items-center rounded-2xl bg-white font-head text-2xl font-black text-mt-primary">{s.n}</div>
                  <s.icon size={30} className="text-white/50" />
                </div>
                <h3 className="mt-6 font-head text-2xl font-bold">{s.t}</h3>
                <p className="mt-3 text-base leading-relaxed text-white/80">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Button onClick={go} data-testid="trial-cta" className="gap-2 bg-mt-accent px-8 py-6 text-base font-black text-mt-text hover:bg-mt-accent/90">
              Start my free 10-day trial <ArrowRight size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="mx-auto max-w-lg px-5 py-16 md:py-20">
        <div className="overflow-hidden rounded-2xl border border-mt-border bg-mt-card shadow-sm">
          <div className="bg-mt-text p-8 text-white">
            <h2 className="font-head text-2xl font-black tracking-tighter">One racer. One price.</h2>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-mono text-5xl font-black">$24.95</span>
              <span className="text-sm text-white/60">one-time</span>
            </div>
            <p className="mt-1 text-sm text-white/60">No subscription. No renewals. Ever.</p>
          </div>
          <div className="p-6">
            <ul className="space-y-3">
              {["10-day free trial, full access", "Save unlimited runs — forever", "Season analytics & trends", "Virtual Pit Crew invites", "Weather auto-fill", "Works in any browser — nothing to install"].map((x) => (
                <li key={x} className="flex items-start gap-2 text-sm"><Check size={18} className="mt-0.5 shrink-0 text-mt-success" /><span>{x}</span></li>
              ))}
            </ul>
            <Button onClick={go} data-testid="pricing-start-trial" className="mt-6 w-full gap-2 bg-mt-primary py-6 text-base font-bold text-white hover:bg-mt-primary/90">
              Start free trial <ArrowRight size={18} />
            </Button>
            <p className="mt-3 flex items-center justify-center gap-1 text-center text-xs text-mt-mutedfg">
              <ShieldCheck size={14} /> Secure PayPal checkout when you're ready to buy
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA band */}
      <section className="relative overflow-hidden bg-mt-text py-16 text-white">
        <CheckerBackground />
        <div className="relative mx-auto max-w-3xl px-5 text-center">
          <Flag size={36} className="mx-auto text-mt-accent" />
          <h2 className="mt-4 font-head text-3xl font-black tracking-tighter sm:text-4xl">Your next pass could be your best. Track it.</h2>
          <p className="mx-auto mt-3 max-w-lg text-base text-white/75">Start logging free today. See your season take shape before you spend a dime.</p>
          <Button onClick={go} data-testid="final-cta" className="mt-7 gap-2 bg-mt-primary px-8 py-6 text-base font-bold text-white hover:bg-mt-primary/90">
            Start your free 10-day trial <ArrowRight size={18} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-mt-border">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 py-8 text-sm text-mt-mutedfg sm:flex-row">
          <div className="flex items-center gap-2">
            <img src={ICON} alt="MyTrack" className="h-7 w-7 rounded-md" />
            <span className="font-head font-black text-mt-text">MyTrack<span className="text-mt-primary">App</span></span>
          </div>
          <span>© {new Date().getFullYear()} MyTrackApp · Log every run. Improve every pass.</span>
        </div>
      </footer>
    </div>
  );
}
